// components/VehicleTrendHighChart.tsx
import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Fade,
  useTheme,
  Chip,
  useMediaQuery,
  Skeleton
} from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import CountUp from 'react-countup';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import PieChartRoundedIcon from '@mui/icons-material/PieChartRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import axiosInstance from '../services/axiosInstance';

// --- TYPE DEFINITIONS ---
type GradientStop = [number, string];

// UPDATED: Vibrant Gradients
const colorPalette: Highcharts.GradientColorObject[] = [
  {
    // Gradient 1: Orange -> Yellow (#FC4A1A -> #F7B733)
    linearGradient: { x1: 0, y1: 1, x2: 0, y2: 0 },
    stops: [[0, '#FF4A1A'], [1, '#FEAB40']] as GradientStop[]
  },
  {
    // Gradient 2: Blue -> Yellow (#00C3FF -> #FFFF1C)
    linearGradient: { x1: 0, y1: 1, x2: 0, y2: 0 },
    stops: [[0, '#3687E8'], [1, '#8EE0ED']] as GradientStop[]
  },
  {
    // Gradient 3: Pink -> Cyan (#EF32D9 -> #89FFFD)
    linearGradient: { x1: 0, y1: 1, x2: 0, y2: 0 },
    stops: [[0, '#D455F2'], [1, '#F9B8D9']] as GradientStop[]
  }
];

// UPDATED: Solid colors derived from the gradients for Pie Charts
const solidPalette = [
  '#FC4A1A', // Bright Orange
  '#00C3FF', // Cyan Blue
  '#EF32D9', // Deep Pink
  '#F7B733', // Warm Yellow
  '#FFFF1C', // Electric Yellow
  '#89FFFD'  // Pale Cyan
];

const chartModes = [
  'Vehicle Sales by Class',
  'Vehicle Sales by Industry',
  'Vehicle Views by Type'
] as const;
type ChartMode = typeof chartModes[number];

type CustomPoint = Highcharts.Point & {
  month: string;
  vehicleType: string;
};

// --- HELPER: Shared Chart Styles ---
const getCommonChartOptions = (isMobile: boolean, height: number): Highcharts.Options => ({
  chart: {
    backgroundColor: 'transparent',
    height: height,
    style: { fontFamily: 'Roboto, sans-serif' },
    spacingTop: 10,
    spacingBottom: 5
  },
  title: { text: undefined },
  credits: { enabled: false },
  yAxis: {
    title: { text: undefined },
    gridLineColor: '#f0f0f0',
    gridLineDashStyle: 'Dash',
    labels: { style: { color: '#bbb', fontSize: '10px' } }
  },
  xAxis: {
    lineColor: 'transparent',
    tickColor: 'transparent',
    labels: {
      style: { color: '#888', fontSize: isMobile ? '9px' : '11px', fontWeight: '500' },
      autoRotation: [-45, 0]
    }
  },
  tooltip: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 8,
    shadow: { opacity: 0.1 },
    borderWidth: 0,
    style: { fontSize: '12px' }
  }
});

const VehicleTrendHighChart = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // ADJUSTED HEIGHT: Decreased a bit as requested
  // Desktop: 340 -> 290
  // Mobile: 260 -> 240
  const chartHeight = isMobile ? 240 : 290;

  // --- STATE ---
  const [loading, setLoading] = useState(true);
  const [chartMode, setChartMode] = useState<ChartMode>('Vehicle Sales by Class');
  
  // Data Stores
  const [dataByType, setDataByType] = useState<any[]>([]);
  const [dataByIndustry, setDataByIndustry] = useState<any[]>([]);
  const [vehicleViewsByType, setVehicleViewsByType] = useState<any[]>([]);
  const [topIndustries, setTopIndustries] = useState<string[]>([]);
  
  // Drill-down States
  const [pieChartData, setPieChartData] = useState<{ month: string; vehicleType: string } | null>(null);
  const [pieBreakdownData, setPieBreakdownData] = useState<{ name: string; y: number }[]>([]);
  
  const [industryPieChartData, setIndustryPieChartData] = useState<{ month: string; industry: string } | null>(null);
  const [industryPieBreakdownData, setIndustryPieBreakdownData] = useState<{ name: string; y: number }[]>([]);
  
  const [selectedIndustryMonth, setSelectedIndustryMonth] = useState<string | null>(null);

  // --- TOTAL CALCULATION ---
  const totalValue = (() => {
    if (chartMode === 'Vehicle Sales by Class') {
      return dataByType.reduce((sum, d) => sum + d.data.reduce((acc: number, item: any) => acc + (item.total || 0), 0), 0);
    }
    if (chartMode === 'Vehicle Sales by Industry') {
      return dataByIndustry.reduce((sum, d) => sum + d.data.reduce((acc: number, entry: any) => acc + (entry.total || 0), 0), 0);
    }
    if (chartMode === 'Vehicle Views by Type') {
      return vehicleViewsByType.reduce((sum, v) => sum + (v.total || 0), 0);
    }
    return 0;
  })();

  const classTypes = ['Light Commercial', 'Heavy Commercial'];

  // --- DATA FETCHING ---
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await axiosInstance.post("/CustomerVehicle/VehicleGetAnalytics");
        const result = response.data as any;

        if (result.responseCode === "00" && result.responseData) {
          const formattedTypeData = (result.responseData.vehicleSalesByType || []).map((monthObj: any) => ({
            month: monthObj.month,
            data: (monthObj.data || []).map((entry: any) => ({
              classType: entry.type,
              total: entry.total,
              vehicleBodyType: entry.vehicleBodyType,
            })),
          }));

          const formattedIndustryData = (result.responseData.vehicleSalesByIndustry || []).map((monthObj: any) => ({
            month: monthObj.month,
            data: (monthObj.data || []).map((entry: any) => ({
              industry: entry.type,
              total: entry.total,
              vehicleTypes: entry.vehicleBodyType,
            })),
          }));

          setDataByType(formattedTypeData);
          setDataByIndustry(formattedIndustryData);
          setVehicleViewsByType(result.responseData.vehicleViewByType || []);

          const industryTotals: Record<string, number> = {};
          for (const month of formattedIndustryData) {
            for (const entry of month.data || []) {
              const ind = entry.industry;
              industryTotals[ind] = (industryTotals[ind] || 0) + (entry.total || 0);
            }
          }
          const top = Object.entries(industryTotals)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([name]) => name);

          setTopIndustries(top);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  // --- CHART OPTIONS ---

  // 1. SALES BY CLASS (Column)
  const salesByClassOptions: Highcharts.Options = {
    ...getCommonChartOptions(isMobile, chartHeight),
    chart: { type: 'column', height: chartHeight, backgroundColor: 'transparent' },
    xAxis: {
      ...getCommonChartOptions(isMobile, chartHeight).xAxis,
      categories: dataByType.map((d) => d.month),
    },
    tooltip: { shared: true, borderRadius: 8 },
    colors: colorPalette,
    plotOptions: {
      column: {
        borderRadius: 6,
        borderWidth: 0,
        pointPadding: 0.1,
        groupPadding: 0.2,
        cursor: 'pointer',
        point: {
          events: {
            click: function () {
              const p = this as CustomPoint;
              const mData = dataByType.find(d => d.month === p.month);
              const cData = mData?.data?.find((c: any) => c.classType === p.vehicleType);
              if (cData) {
                const breakdown = cData.vehicleBodyType.map((bt: any) => ({ name: bt.type, y: bt.total }));
                setPieChartData({ month: p.month, vehicleType: p.vehicleType });
                setPieBreakdownData(breakdown);
              }
            }
          }
        }
      }
    },
    series: classTypes.map(type => ({
      type: 'column',
      name: type,
      data: dataByType.map(d => {
        const found = d.data.find((c: any) => c.classType === type);
        return { y: found?.total || 0, month: d.month, vehicleType: type };
      })
    })),
    legend: { itemStyle: { color: '#666', fontSize: '11px', fontWeight: '500' } }
  };

  // 2. SALES BY INDUSTRY (Level 1: Column - Top 3)
  const barChartIndustryOptions: Highcharts.Options = {
    ...getCommonChartOptions(isMobile, chartHeight),
    chart: { type: 'column', height: chartHeight, backgroundColor: 'transparent' },
    xAxis: { 
      ...getCommonChartOptions(isMobile, chartHeight).xAxis,
      categories: dataByIndustry.map((d) => d.month),
    },
    tooltip: { shared: true },
    colors: colorPalette,
    plotOptions: {
      column: {
        borderRadius: 6,
        borderWidth: 0,
        cursor: 'pointer',
        point: {
          events: {
            click: function () {
              // Level 1 -> Level 2 (Specific Month, All Industries)
              const cMonth = String(this.category);
              const mData = dataByIndustry.find(d => d.month === cMonth);
              if (!mData) return;
              const indData = mData.data || [];
              const breakdown = indData.map((e: any) => ({ name: e.industry, y: e.total }));
              
              setSelectedIndustryMonth(cMonth);
              setIndustryPieChartData({ month: cMonth, industry: 'All Industries' });
              setIndustryPieBreakdownData(breakdown);
            }
          }
        }
      }
    },
    series: topIndustries.map(ind => ({
      type: 'column',
      name: ind,
      data: dataByIndustry.map(mData => {
        const match = mData.data.find((i: any) => i.industry === ind);
        return match?.total || 0;
      })
    })),
    legend: { itemStyle: { color: '#666', fontSize: '11px', fontWeight: '500' } }
  };

  // 3. PIE CHARTS (Dynamic Drill Down)
  const generatePieOptions = (
      title: string, 
      data: any[], 
      isIndustryMode: boolean = false
    ): Highcharts.Options => ({
    ...getCommonChartOptions(isMobile, chartHeight),
    chart: { 
        type: 'pie', 
        height: chartHeight, 
        backgroundColor: 'transparent',
        // ADJUSTED: Reduced spacing to fit better in smaller height
        spacingTop: 0, 
        spacingBottom: 0,
        marginTop: 0
    },
    title: { 
        text: title, 
        align: 'left',
        style: { fontSize: '14px', fontWeight: 'bold', color: '#333' },
        y: 10 // Pushed title down slightly
    },
    tooltip: { pointFormat: '{point.name}: <b>{point.percentage:.1f}%</b> ({point.y})' },
    plotOptions: {
        pie: {
            // ADJUSTED: Lifted center up to '45%' to "make up a bit"
            center: ['50%', '45%'],
            innerSize: '50%',
            size: '75%', // Ensure it fits in reduced height
            borderWidth: 0,
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
                enabled: true,
                format: '<b>{point.name}</b>',
                style: { fontSize: isMobile ? '9px' : '10px', color: '#555', textOutline: 'none' },
                distance: 10
            },
            colors: solidPalette,
            events: {
                click: function (event) {
                    if (isIndustryMode && industryPieChartData?.industry === 'All Industries') {
                        const clickedIndustryName = event.point.name;
                        const monthData = dataByIndustry.find((d) => d.month === industryPieChartData.month);
                        const specificIndData = monthData?.data.find((i: any) => i.industry === clickedIndustryName);
                        
                        if (specificIndData && specificIndData.vehicleTypes) {
                            const breakdown = specificIndData.vehicleTypes.map((v: any) => ({ name: v.type, y: v.total }));
                            
                            if (breakdown.length > 0) {
                                setIndustryPieChartData({ 
                                    month: industryPieChartData.month, 
                                    industry: clickedIndustryName 
                                });
                                setIndustryPieBreakdownData(breakdown);
                            } else {
                                // No further breakdown available
                                return;
                            }
                        }
                    }
                }
            }
        }
    },
    series: [{ type: 'pie', name: 'Units', data }]
  });

  const pieChartOptions = pieChartData 
    ? generatePieOptions(`${pieChartData.vehicleType} Types`, pieBreakdownData)
    : null;

  const industryPieChartOptions = industryPieChartData
    ? generatePieOptions(
        industryPieChartData.industry === 'All Industries' 
          ? `Industries in ${industryPieChartData.month}`
          : `${industryPieChartData.industry} Breakdown`,
        industryPieBreakdownData,
        true
      )
    : null;

  // 4. VIEWS BY TYPE (Bar)
  const barChartViewByTypeOptions: Highcharts.Options = {
    ...getCommonChartOptions(isMobile, chartHeight),
    chart: { type: 'bar', height: chartHeight, backgroundColor: 'transparent' },
    xAxis: {
      categories: vehicleViewsByType.map(v => v.type),
      lineWidth: 0,
      labels: { style: { fontWeight: 'bold', color: '#333', fontSize: isMobile ? '10px' : '11px' } }
    },
    yAxis: { visible: false },
    legend: { enabled: false },
    plotOptions: {
      bar: {
        borderRadius: 6,
        colorByPoint: true,
        dataLabels: { enabled: true, align: 'right' },
        borderWidth: 0
      }
    },
    colors: colorPalette,
    series: [{ type: 'bar', name: 'Views', data: vehicleViewsByType.map(v => v.total) }],
  };

  const handleModeChange = (mode: ChartMode) => {
    setChartMode(mode);
    setPieChartData(null);
    setIndustryPieChartData(null);
    setSelectedIndustryMonth(null);
  };

  const handleIndustryBack = () => {
    if (industryPieChartData?.industry !== 'All Industries' && selectedIndustryMonth) {
        const mData = dataByIndustry.find(d => d.month === selectedIndustryMonth);
        if (mData) {
            const br = mData.data.map((e: any) => ({ name: e.industry, y: e.total }));
            setIndustryPieChartData({ month: selectedIndustryMonth, industry: 'All Industries' });
            setIndustryPieBreakdownData(br);
        }
    } else {
        setIndustryPieChartData(null);
        setSelectedIndustryMonth(null);
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 1. HEADER ROW */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={800} sx={{ color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpRoundedIcon sx={{ color: '#6a1b9a' }} />
            Market Insights
          </Typography>
          {!isMobile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.85rem' }}>
                Real-time trends on sales & popularity.
              </Typography>
          )}
        </Box>

        <Box sx={{ px: 2.5, py: 1, borderRadius: '16px', bgcolor: '#f7e2ba', border: '1px solid #e1bee7', textAlign: 'center', minWidth: '120px' }}>
          <Typography variant="caption" fontWeight={700} sx={{ color: '#040404ff', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5, display: 'block' }}>
            {chartMode === 'Vehicle Views by Type' ? 'TOTAL VIEWS' : 'TOTAL UNITS'}
          </Typography>
          
          {loading ? (
             <Skeleton width={80} height={30} sx={{ mx: 'auto' }} />
          ) : (
            <Typography variant="h4" fontWeight={800} sx={{ color: '#2C3E50', lineHeight: 1, fontSize: isMobile ? '1.5rem' : '2rem' }}>
                <CountUp end={totalValue} duration={1.5} separator="," />
            </Typography>
          )}
        </Box>
      </Box>

      {/* 2. TABS ROW - Compact */}
      <Box sx={{ width: '100%', overflowX: 'auto', mb: 2, pb: 0.5 }}>
        <Stack direction="row" spacing={1} sx={{ minWidth: 'fit-content' }}>
          {[
            { mode: 'Vehicle Sales by Class', icon: <TrendingUpRoundedIcon fontSize="small"/> },
            { mode: 'Vehicle Sales by Industry', icon: <BarChartRoundedIcon fontSize="small"/> },
            { mode: 'Vehicle Views by Type', icon: <PieChartRoundedIcon fontSize="small"/> }
          ].map((item) => {
            const isSelected = chartMode === item.mode;
            return (
              <Chip
                key={item.mode}
                label={item.mode.replace('Vehicle ', '')}
                icon={item.icon}
                onClick={() => handleModeChange(item.mode as ChartMode)}
                clickable
                sx={{
                  fontWeight: isSelected ? 700 : 500,
                  bgcolor: isSelected ? '#FFB800' : '#fff',
                  color: isSelected ? '#fff' : '#666',
                  border: isSelected ? 'none' : '1px solid #e0e0e0',
                  boxShadow: isSelected ? '0 4px 10px rgba(106, 27, 154, 0.2)' : 'none',
                  '&:hover': { bgcolor: isSelected ? '#FFB800' : '#f5f5f5' },
                  '& .MuiChip-icon': { color: isSelected ? '#fff' : '#666' },
                  height: 32
                }}
              />
            );
          })}
        </Stack>
      </Box>

      {/* 3. CHART AREA */}
      <Box sx={{ flexGrow: 1, width: '100%', position: 'relative', minHeight: chartHeight }}>
        <Fade in={true} timeout={400} key={chartMode}>
          <Box sx={{ width: '100%' }}>
            
            {/* -- Sales by Class -- */}
            {chartMode === 'Vehicle Sales by Class' && (
              pieChartData ? (
                <>
                  <Button 
                    size="small" 
                    startIcon={<ArrowBackRoundedIcon />} 
                    onClick={() => setPieChartData(null)}
                    sx={{ mb: 1, color: '#666', textTransform: 'none', fontWeight: 700 }}
                  >
                    Back
                  </Button>
                  <HighchartsReact highcharts={Highcharts} options={pieChartOptions!} />
                </>
              ) : (
                <HighchartsReact highcharts={Highcharts} options={salesByClassOptions} />
              )
            )}

            {/* -- Sales by Industry (Nested Deep Dive) -- */}
            {chartMode === 'Vehicle Sales by Industry' && (
              industryPieChartData ? (
                <>
                  <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} mb={1}>
                    <Button 
                      size="small" 
                      startIcon={<ArrowBackRoundedIcon />} 
                      onClick={handleIndustryBack} // SMART BACK HANDLER
                      sx={{ color: '#666', textTransform: 'none', fontWeight: 700, minWidth: 'auto' }}
                    >
                      Back
                    </Button>
                    <Chip 
                        label={
                            industryPieChartData.industry === 'All Industries'
                            ? `${industryPieChartData.month}`
                            : `${industryPieChartData.month} / ${industryPieChartData.industry}`
                        } 
                        size="small" 
                        sx={{ bgcolor: '#ede7f6', color: '#5e35b1', fontWeight: 'bold' }} 
                    />
                  </Stack>
                   
                  <HighchartsReact highcharts={Highcharts} options={industryPieChartOptions!} />
                </>
              ) : (
                <HighchartsReact highcharts={Highcharts} options={barChartIndustryOptions} />
              )
            )}

            {/* -- Views by Type -- */}
            {chartMode === 'Vehicle Views by Type' && (
                 <HighchartsReact highcharts={Highcharts} options={barChartViewByTypeOptions} />
            )}
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default VehicleTrendHighChart;